import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useNavigate } from 'react-router-dom';
import Upload from './components/Upload';
import PdfList from './components/PdfList';
import PdfDetails from './components/PdfDetails';
import ManageAccess from './components/ManageAccess';
import Login from './components/Login';
import RegisterUser from './components/RegisterUser';
import ResetPassword from './components/ResetPassword';
import PasswordResetRequest from './components/PasswordResetRequest';
import AdminDashboard from './components/AdminDashboard';
import PaymentReturn from './components/PaymentReturn';
import SetupReturn from './components/SetupReturn';
import './App.css';
import logo from './assets/logo.png';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const isTokenExpired = (token) => {
    if (!token) return true;
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp;
    const currentTime = Math.floor(Date.now() / 1000);
    return exp < currentTime;
  };

  const refreshToken = async () => {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) return false;
    try {
      const response = await fetch(`${process.env.REACT_APP_FQDN}/api/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${refresh_token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.access_token);
        return true;
      } else {
        console.error('Failed to refresh token');
        return false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  };

  return (
    <Router>
      <AppContent
        isAuthenticated={isAuthenticated}
        setIsAuthenticated={setIsAuthenticated}
        refreshToken={refreshToken}
        isTokenExpired={isTokenExpired}
      />
    </Router>
  );
}

function AppContent({ isAuthenticated, setIsAuthenticated, refreshToken, isTokenExpired }) {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [groupCredits, setGroupCredits] = useState(null);

  const checkAdminStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_FQDN}/api/check_admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setIsAdmin(!!data.is_admin);
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const getGroupCredits = async (username) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_FQDN}/api/check-group-credits`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username }),
      });
      if (response.ok) {
        const data = await response.json();
        setGroupCredits(data.group_credits);
        // Keep localStorage in sync so PaymentReturn can read the pre-payment balance
        localStorage.setItem('groupCredits', data.group_credits);
      } else {
        console.error('Failed to fetch group credits');
      }
    } catch (error) {
      console.error('Error fetching group credits:', error);
    }
  };

  useEffect(() => {
    const checkAuthentication = async () => {
      const token = localStorage.getItem('token');
      if (token && !isTokenExpired(token)) {
        setIsAuthenticated(true);
        setUsername(localStorage.getItem('username'));
        await checkAdminStatus();
        await getGroupCredits(localStorage.getItem('username'));
      } else {
        const refreshed = await refreshToken();
        if (!refreshed) {
          setIsAuthenticated(false);
          navigate('/login');
        } else {
          setIsAuthenticated(true);
          setUsername(localStorage.getItem('username'));
          await checkAdminStatus();
          await getGroupCredits(localStorage.getItem('username'));
        }
      }
    };

    if (
      window.location.pathname !== '/reset-password' &&
      window.location.pathname !== '/request-password-reset' &&
      window.location.pathname !== '/register'
    ) {
      checkAuthentication();
    }
  }, [navigate, isTokenExpired, refreshToken, setIsAuthenticated]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('username');
    localStorage.removeItem('groupCredits');
    localStorage.removeItem('balanceBeforePayment');
    setIsAuthenticated(false);
    navigate('/login');
  };

  return (
    <div className="App">
      <header className="header-container">
        {isAuthenticated && (
          <div className="welcome-message">
            <button className="logout-button" onClick={handleLogout}>Logout</button>
            Welcome, {username}!{" "}
            {groupCredits !== null && (
              <div className="group-credits">
                Balance: £{(groupCredits / 100).toFixed(2)}
              </div>
            )}
          </div>
        )}
        <a href="/" className="nav-logo-link">
          <img src={logo} alt="Logo" className="nav-logo" />
        </a>

        <nav className="nav-links">
          {isAuthenticated && (
            <>
              <Link className="nav-button" to="/admin-dashboard">Admin Dashboard</Link>
              {isAdmin && (
                <>
                  <Link className="nav-button" to="/upload">Upload</Link>
                  <Link className="nav-button" to="/pdfs">PDF List</Link>
                  <Link className="nav-button" to="/manage-access">MH Admins</Link>
                </>
              )}
            </>
          )}
        </nav>
      </header>

      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/request-password-reset" element={<PasswordResetRequest />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/register" element={<RegisterUser />} />

        {isAuthenticated ? (
          <>
            <Route path="/" element={<AdminDashboard />} />
            <Route path="/admin-dashboard" element={<AdminDashboard />} />
            <Route path="/payment-success" element={<PaymentReturn />} />
            <Route path="/setup-success" element={<SetupReturn />} />

            {isAdmin && (
              <>
                <Route path="/upload" element={<Upload />} />
                <Route path="/pdfs" element={<PdfList />} />
                <Route path="/pdf/:id" element={<PdfDetails />} />
                <Route path="/manage-access" element={<ManageAccess />} />
              </>
            )}
          </>
        ) : (
          <Route path="/" element={<Login />} />
        )}
      </Routes>

      <footer className="footer">
        © {new Date().getFullYear()} Morgan Hill Consultants - All Rights Reserved <br />
        For support, please send an email to support@docxs.co.uk
      </footer>
    </div>
  );
}

export default App;
